import type { Signal } from "@angular/core";
import { S25Datefilter } from "../../modules/s25-dateformat/s25.datefilter.service";
import { S25ItemComponent } from "../../modules/s25-item/s25.item.component";
import { S25FavoriteSimpleComponent } from "../../modules/s25-favorite/s25.favorite.simple.component";
import { GenericTableListComponent } from "../../modules/s25-table/generics/generic.table.list.component";
import { S25TableComponent } from "../../modules/s25-table/s25.table.component";
import type { Table } from "../../modules/s25-table/Table";
import { ListData } from "../../pojo/ListData";
import { UserprefService } from "../../services/userpref.service";
import { s25Resource } from "../../util/s25.resource";
import { S25Util } from "../../util/s25-util";
import type { ListConfig } from "./list.config";
import type {
    ListCellValue,
    ListColumn,
    ListDateFormats,
    ListItemRef,
    ListRow,
    ListTableColumnDefaultsOptions,
    ListRowMapperOptions,
} from "./list.table.types";
import { MaybeArray } from "../../pojo/Util";

type FavoriteCellValue = {
    templateType?: ListData.TemplateType;
    isFav?: number | boolean;
    itemTypeId?: number;
    itemId?: number;
    itemName?: string;
};

/**
 * This class orchestrates table columns and maps cells. Currently, it's bespoke for
 * the list components, but perhaps this logic could be moved elsewhere eventually.
 * Must be constructed in an injection context (i.e. component constructor)
 */
export class ListTableHelper {
    protected readonly dateFormat = s25Resource(() => UserprefService.getS25Dateformat());
    protected readonly timeFormat = s25Resource(() => UserprefService.getS25Timeformat());
    protected readonly dateTimeFormat = s25Resource(() => UserprefService.getS25DateTimeformat());

    constructor(
        protected readonly table: Signal<S25TableComponent | undefined>,
        protected readonly config?: Signal<ListConfig>,
    ) {}

    refreshTable(resetPagination = false): void {
        void this.table()?.refresh(true, resetPagination);
    }

    setColumns(columns: Table.Column[]): void {
        this.table()?.setColumns(columns);
    }

    setVisibleColumns(columnIds: Set<string>): void {
        this.table()?.setVisibleColumns(columnIds);
    }

    setColumnsFromListColumns(cols: ListColumn[], options: ListTableColumnDefaultsOptions = {}): void {
        this.setColumns(cols.map((column) => this.buildTableColumn(column, options)));
    }

    applyColumnVisibility(cols: ListColumn[]): void {
        const visibleColumnIds = new Set(
            cols.filter((column) => column.isVisible !== 0).map((column) => this.getColumnId(column)),
        );
        this.setVisibleColumns(visibleColumnIds);
    }

    getColumnId(column: ListColumn): string {
        if (column.prefname) {
            return column.prefname;
        }
        if (column.prefId) {
            return column.prefId;
        }
        if (column.name === "IsFav") {
            return "isFav";
        }
        return column.name;
    }

    buildTableColumn(column: ListColumn, options: ListTableColumnDefaultsOptions = {}): Table.Column {
        const tableColumn: Table.Column = {
            header: column.name,
            id: this.getColumnId(column),
            sortable: !!column.sortable,
        };
        this.applyTableColumnDefaults(tableColumn, column, options);
        return tableColumn;
    }

    applyTableColumnDefaults(
        tableColumn: Table.Column,
        column: ListColumn,
        options: ListTableColumnDefaultsOptions = {},
    ): void {
        if (this.getColumnId(column) === "isFav") {
            tableColumn.header = "";
            tableColumn.width = "min-content";
            tableColumn.cells = { component: S25FavoriteSimpleComponent };
            return;
        }

        switch (column.prefname) {
            case "event":
            case "event_name":
            case "name":
            case "last_name":
                tableColumn.cells = { component: S25ItemComponent };
                break;
            case "start_date":
            case "first_date":
                tableColumn.minWidth = 60;
                break;
            case "creation_date":
                tableColumn.minWidth = 100;
                break;
            case "instructor":
                if (options.includeInstructorMinWidth) {
                    tableColumn.minWidth = 40;
                }
                break;
        }

        this.config?.()?.cellMapping?.applyTableColumnDefaults?.(this.mappingContext(), tableColumn, column, options);
    }

    mapCell(column: ListColumn, data: ListCellValue, formatsOverride?: ListDateFormats): [string, Table.Cell] {
        const formats = formatsOverride ?? this.formats();
        const columnId = this.getColumnId(column);

        const configCell = this.config?.()?.cellMapping?.mapCell?.(this.mappingContext(), column, data, formats);
        if (configCell) {
            return configCell;
        }

        const favoriteCell = this.getFavoriteCell(columnId, data);
        if (favoriteCell) {
            return favoriteCell;
        }

        const templateText = this.formatListTemplateCell(data, formats);
        if (templateText !== undefined) {
            return [columnId, { text: templateText }];
        }

        const standardCell = this.mapStandardCell(column, columnId, data, formats);
        if (standardCell) {
            return standardCell;
        }

        if (column.isDateTime) {
            return [columnId, { text: S25Datefilter.transform(String(data ?? ""), formats.dateTimeFormat) ?? "" }];
        }

        return this.mapDefaultCell(columnId, data);
    }

    protected mapListDataRow<TRow extends { contextId?: string | number; row: readonly unknown[] }>(
        columns: ListColumn[],
        row: TRow,
        mapCell: (column: ListColumn, data: ListCellValue) => [string, Table.Cell],
        getId: (columns: ListColumn[], row: TRow) => string | number,
        getName: (columns: ListColumn[], row: TRow) => string,
    ): Table.Row {
        const cells: Table.Row["cells"] = {};
        for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
            const [key, cell] = mapCell(columns[columnIndex], row.row[columnIndex] as ListCellValue);
            cells[key] = cell;
        }

        return {
            id: getId(columns, row),
            name: getName(columns, row),
            cells,
        };
    }

    protected defaultRowIdMapFn(
        this: never,
        _columns: ListColumn[],
        row: { contextId?: string | number; row: readonly unknown[] },
    ): string | number {
        return (row as ListRow).contextId;
    }

    protected defaultRowNameMapFn(
        this: never,
        columns: ListColumn[],
        row: { contextId?: string | number; row: readonly unknown[] },
    ): string {
        const nameColumnIndex = columns.findIndex(
            (column) => column.prefname === "event_name" || column.prefname === "event",
        );
        const nameCell = nameColumnIndex >= 0 ? row.row[nameColumnIndex] : undefined;
        return typeof nameCell === "object" && nameCell && "itemName" in nameCell
            ? String(nameCell.itemName)
            : String(row.contextId);
    }

    mapToRow<TRow extends { contextId?: string | number; row: readonly unknown[] }>(
        columns: ListColumn[],
        row: TRow,
        options?: ListRowMapperOptions<TRow>,
        formatsOverride?: ListDateFormats,
    ): Table.Row {
        const configRowMapperOptions = this.config?.()?.cellMapping?.rowMapperOptions;

        return this.mapListDataRow(
            columns,
            row,
            (column, data) => this.mapCell(column, data, formatsOverride),
            options?.getId ?? configRowMapperOptions?.getId ?? this.defaultRowIdMapFn,
            options?.getName ?? configRowMapperOptions?.getName ?? this.defaultRowNameMapFn,
        );
    }

    mapRows<TRow extends { contextId?: string | number; row: readonly unknown[] }>(
        columns: ListColumn[],
        rows: TRow[],
    ): Table.Row[] {
        return rows.map((row) => this.mapToRow(columns, row));
    }

    protected formats(): ListDateFormats {
        return {
            dateFormat: this.dateFormat.value() ?? "",
            timeFormat: this.timeFormat.value() ?? "",
            dateTimeFormat: this.dateTimeFormat.value() ?? "",
        };
    }

    private mappingContext() {
        return {
            refreshTable: () => this.refreshTable(),
            getColumnId: (column: ListColumn) => this.getColumnId(column),
        };
    }

    private mapStandardCell(
        column: ListColumn,
        columnId: string,
        data: ListCellValue,
        formats: ListDateFormats,
    ): [string, Table.Cell] | undefined {
        switch (column.prefname) {
            case "event":
                if (typeof data === "object" && data && !("itemName" in data && data.itemName)) {
                    return [column.prefname, { component: undefined, text: "none", className: "italic" }];
                }
            // fallthrough when task event is linked
            case "event_name":
            case "name":
            case "last_name":
                return this.getItemCell(column.prefname, data);
            case "bldg_name":
                return [
                    column.prefname,
                    {
                        text:
                            typeof data === "object" && data && "itemName" in data ? data.itemName : String(data ?? ""),
                    },
                ];
            case "locations":
            case "resources":
            case "organization":
            case "organizations": {
                if (typeof data === "object" && data && "subject" in data && Array.isArray(data.subject)) {
                    let items: MaybeArray<Table.Cell<S25ItemComponent>[]>;
                    if (column.prefname === "organization") {
                        const primary = data.subject.filter((item) => item.isPrimary);
                        const additional = data.subject.filter((item) => !item.isPrimary);
                        items = [
                            primary.map((item) => this.listItemToCell(item)),
                            additional.map((item) => this.listItemToCell(item)),
                        ];
                    } else {
                        items = data.subject.map((item) => this.listItemToCell(item));
                    }
                    return [
                        column.prefname,
                        {
                            component: GenericTableListComponent,
                            inputs: { items, hasDecoration: false },
                        },
                    ];
                }
                if (typeof data === "object" && data && "templateType" in data) {
                    return [
                        column.prefname,
                        { component: GenericTableListComponent, inputs: { items: [], hasDecoration: false } },
                    ];
                }
                return [column.prefname, { text: String(data ?? "") }];
            }
            case "start_date":
            case "first_date":
                return [column.prefname, { text: S25Datefilter.transform(String(data), formats.dateFormat)! }];
            case "start_time":
                return [column.prefname, { text: S25Datefilter.transform(String(data), formats.timeFormat)! }];
            case "creation_date":
                return [column.prefname, { text: S25Datefilter.transform(String(data), formats.dateTimeFormat)! }];
            case "state_name":
                return [column.prefname, { text: String(data ?? ""), className: `state${data}` }];
            default:
                return undefined;
        }
    }

    private listItemToCell(item: ListItemRef): Table.Cell<S25ItemComponent> {
        return {
            component: S25ItemComponent,
            inputs: {
                itemTypeId: item.itemTypeId,
                itemId: item.itemId,
                itemName: item.itemName,
            },
        } satisfies Table.Cell<S25ItemComponent>;
    }

    private mapDefaultCell(columnId: string, data: ListCellValue): [string, Table.Cell] {
        if (typeof data === "object" && data) {
            return [columnId, { text: "" }];
        }
        return [columnId, { text: String(data ?? "") }];
    }

    private getItemCell(columnId: string, data: ListCellValue): [string, Table.Cell] {
        if (typeof data === "object" && data && "itemName" in data) {
            return [
                columnId,
                {
                    inputs: {
                        itemTypeId: data.itemTypeId,
                        itemId: data.itemId,
                        itemName: data.itemName,
                    },
                },
            ];
        }
        return [columnId, { text: String(data ?? "") }];
    }

    private getFavoriteCell(columnId: string, data: ListCellValue): [string, Table.Cell] | undefined {
        if (typeof data !== "object" || !data) {
            return undefined;
        }
        if (!("isFav" in data) && !("templateType" in data && data.templateType === ListData.TemplateType.Favorite)) {
            return undefined;
        }

        const favoriteCell = data as FavoriteCellValue;
        return [
            columnId,
            {
                inputs: {
                    starred: !!favoriteCell.isFav,
                    itemId: favoriteCell.itemId,
                    itemType: favoriteCell.itemTypeId,
                    itemName: favoriteCell.itemName ?? "",
                    loggedIn: true,
                },
            },
        ];
    }

    private formatListTemplateCell(data: ListCellValue, formats: ListDateFormats): string | undefined {
        if (typeof data !== "object" || !data || !("templateType" in data)) {
            return undefined;
        }

        const templateType = data.templateType;
        if (templateType === ListData.TemplateType.TimeRange) {
            return this.formatListTimeRangeCell(data, formats);
        }
        if (templateType === ListData.TemplateType.TimeDate) {
            return this.formatListTimeDateCell(data, formats);
        }

        return undefined;
    }

    private formatListTimeRangeCell(cell: { startDate?: string; endDate?: string }, formats: ListDateFormats): string {
        if (!cell.startDate || !cell.endDate) {
            return "";
        }

        const startDate = S25Util.date.parse(cell.startDate);
        const endDate = S25Util.date.parse(cell.endDate);
        const startTime = S25Datefilter.transform(startDate, formats.timeFormat) ?? "";
        const endTime = S25Datefilter.transform(endDate, formats.timeFormat) ?? "";
        const dateSuffix = !S25Util.date.equalDate(startDate, endDate)
            ? ` (${S25Datefilter.transform(endDate, formats.dateFormat) ?? ""})`
            : "";

        return `${startTime} - ${endTime}${dateSuffix}`;
    }

    private formatListTimeDateCell(cell: { itemName?: string; context?: string }, formats: ListDateFormats): string {
        if (!cell.itemName) {
            return "";
        }

        const context = cell.context || cell.itemName;
        const itemDate = S25Util.date.parse(cell.itemName);
        const contextDate = S25Util.date.parse(context);
        const time = S25Datefilter.transform(itemDate, formats.timeFormat) ?? "";
        const dateSuffix = !S25Util.date.equalDate(itemDate, contextDate)
            ? ` (${S25Datefilter.transform(itemDate, formats.dateFormat) ?? ""})`
            : "";

        return `${time}${dateSuffix}`;
    }
}
